/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  RadioButton,
  RadioButtonGroup,
  TextInput,
  Toggle,
  Tooltip
} from '@carbon/react';
import { Help } from '@carbon/react/icons';
import React, { useState, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { ctaEvents } from '../../../../ia/util/trackers/segment';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { SEGMENT } from '../../../trackers/trackEvents';
import { CONTACT_OPTIONS } from '../../../util/validation/constants';
import { NAMESPACES, TOOLING_TYPES } from '../../../util/web-chat';
import { TabContent } from '../../common/tabbed-header/TabContent';
import PreviewWebChat from '../../preview-web-chat/PreviewWebChat';
import { links } from '../links';
import { SuggestionsTab as messages } from '../messages';
import { FIELDS } from '../schema';

import './Suggestions.scss';

interface SuggestionsProps {
  /**
   *
   */
  tabname?: string;
  /**
   *
   */
  environmentId?: string;
  /**
   *
   */
  integrationId: string;
  /**
   *
   */
  readOnly?: boolean;
}
export const Suggestions = ({
  environmentId,
  integrationId,
  readOnly
}: SuggestionsProps) => {
  const { formatMessage: t } = useIntl();
  const formMethods = useFormContext();
  const webChatContainer = useRef(null);

  const { control } = formMethods;

  // Fieldnames
  const { getFieldValue, registerInputField } = useFormHelpers(formMethods);

  const contactLabelChangeState = useState<boolean>(false);
  const [toggled, setToggled] = useState<boolean>(
    getFieldValue(FIELDS.ALTERNATE_SUGGESTIONS.IS_ON)
  );

  const onToggle = boolean => {
    setToggled(boolean);
    ctaEvents.webChatSuggestionsToggle({
      category: 'Suggestions',
      resultValue: boolean
        ? SEGMENT.EVENTS.WEB_CHAT_SUGGESTIONS_ENABLED
        : SEGMENT.EVENTS.WEB_CHAT_SUGGESTIONS_DISABLED
    });
  };

  return (
    <TabContent
      ImageComponent={() => <Help size={32} />}
      title={<FormattedMessage {...messages.title} />}
      subtitle={t(messages.subtitle)}
      learnMoreLink={
        useIsNewIA()
          ? links.learnMoreSuggestionsLinkNewIA
          : links.learnMoreSuggestionsLinkClassic
      }>
      <div className="Suggestions">
        <div className="TabContent__field-container">
          <Controller
            control={control}
            name={FIELDS.ALTERNATE_SUGGESTIONS.IS_ON}
            render={({ field: { onChange, value } }) => (
              <Toggle
                disabled={readOnly}
                id={FIELDS.ALTERNATE_SUGGESTIONS.IS_ON}
                onToggle={event => {
                  onToggle(!value);
                  onChange(event);
                }}
                toggled={value}
                aria-label={t(messages.enable)}
                size="sm"
              />
            )}
          />
        </div>
        {toggled && (
          <div>
            <div className="TabContent__field-container">
              <h5 className="TabContent__inner-title TabContent__inner-title-heading">
                <FormattedMessage {...messages.connectCustomersTitle} />
              </h5>
              <p>
                <FormattedMessage {...messages.connectCustomersDescription} />
              </p>
            </div>
            <Controller
              control={control}
              name={FIELDS.ALTERNATE_SUGGESTIONS.CONTACT_OPTION}
              render={({ field: { onChange, value } }) => (
                <RadioButtonGroup
                  className="Suggestions_radio-group"
                  disabled={readOnly}
                  name={FIELDS.ALTERNATE_SUGGESTIONS.CONTACT_OPTION}
                  legendText={t(messages.contactOptionLabel)}
                  orientation="vertical"
                  valueSelected={value}>
                  <RadioButton
                    id="suggestions_contact-option-always"
                    value={CONTACT_OPTIONS.ALWAYS}
                    labelText={t(messages.contactOptionAlways)}
                    onClick={e =>
                      onChange((e.target as HTMLTextAreaElement).value)
                    }
                  />
                  <RadioButton
                    id="suggestions_contact-option-never"
                    value={CONTACT_OPTIONS.NEVER}
                    labelText={t(messages.contactOptionNever)}
                    onClick={e =>
                      onChange((e.target as HTMLTextAreaElement).value)
                    }
                  />
                  <RadioButton
                    id="suggestions_contact-option-one-strike"
                    value={CONTACT_OPTIONS.ONE_STRIKE}
                    labelText={
                      <div data-floating-menu-container>
                        <FormattedMessage
                          {...messages.contactOptionFailedAttempt}
                        />
                        <Tooltip
                          triggerId="suggestions_contact-option-one-strike-tooltip"
                          triggerText=""
                          showIcon={true}
                          iconDescription={t(messages.moreInformationLabel)}
                          iconName="icon--info"
                          align="top"
                          description={t(messages.failedAttemptTooltip)}>
                          <Help />
                        </Tooltip>
                      </div>
                    }
                    onClick={e =>
                      onChange((e.target as HTMLTextAreaElement).value)
                    }
                  />
                </RadioButtonGroup>
              )}
            />
            <div className="TabContent__field-container">
              <TextInput
                {...registerInputField({
                  field: FIELDS.ALTERNATE_SUGGESTIONS.CONTACT_LABEL,
                  labelText: messages.optionLabel,
                  helperText: messages.optionLabelHelperText,
                  placeholderText: messages.optionLabelPlaceholder,
                  changeState: contactLabelChangeState,
                  readOnly
                })}
                className="TabContent__field"
              />
            </div>
          </div>
        )}
      </div>
      <div className="Suggestions__webchat">
        <PreviewWebChat
          integrationId={integrationId}
          containerRef={webChatContainer}
          environmentId={environmentId}
          namespace={NAMESPACES.CHANNEL_PAGE}
          removeDefaultFocus={true}
          showRestart={true}
          toolingType={TOOLING_TYPES.WEB_CHAT}
          useDraftConfig
          disableSessionHistory
        />
        <div className="container" ref={webChatContainer} />
      </div>
    </TabContent>
  );
};
